import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import GrandfatherBanner from "@/components/GrandfatherBanner";
import FoundingPromoBanner from "@/components/FoundingPromoBanner";
import { CheckCircleIcon as CheckCircle, StarIcon as Star, CrownIcon as Crown, ShieldStarIcon as ShieldStar, SparkleIcon as Sparkle, ArrowRightIcon as ArrowRight, CaretDownIcon as CaretDown, HeartStraightIcon as HeartStraight, MagnifyingGlassIcon as MagnifyingGlass, TruckIcon as Truck, PawPrintIcon as PawPrint, ScissorsIcon as Scissors, StorefrontIcon as Storefront } from "@phosphor-icons/react";
import { NoiseOverlay, GoldDivider } from "@/components/luxury";
import { ReaderAppBanner } from "@/components/ReaderAppBanner";
import { isNativeApp } from "@/lib/nativeBridge";
import NativePlanNotice from "@/components/NativePlanNotice";

/**
 * Public `/pricing` page — restructured around personas.
 *
 * The earlier single-track design ("Basic / Pro / Kennel Elite") was
 * breeder-only and felt unwelcoming to the platform's three other
 * personas (buyers, dog owners using Dog Log, transporters). This
 * version puts a persona switcher at the top so every visitor sees
 * the pricing path that matches their actual situation in two
 * clicks. The persona detection also pre-selects from a `?for=…`
 * query param so we can deep-link from marketing / persona-specific
 * landing pages.
 */

const PERSONAS = [
  {
    id: "breeder",
    label: "I'm a breeder",
    desc: "Register dogs, breed, sell from your kennel.",
    icon: Star,
    eyebrow: "For breeders & kennels",
    headline: "One subscription. Every breeding tool.",
    blurb: "Pick the plan that matches your breeding cadence. Registration fees ($25/dog · $75/litter) are flat. Marketplace listings free or discounted on every tier.",
  },
  {
    id: "buyer",
    label: "I'm a buyer",
    desc: "Looking for my next dog.",
    icon: MagnifyingGlass,
    eyebrow: "For buyers",
    headline: "Find the right dog without being rate-limited.",
    blurb: "Browsing is always free. Buyer Pass removes the inquiry cap, unlocks pedigree depth, and gets your messages prioritized in breeder inboxes.",
  },
  {
    id: "owner",
    label: "I'm a dog owner",
    desc: "Track the dog I already have.",
    icon: HeartStraight,
    eyebrow: "For dog owners",
    headline: "Care for the dog you love — one log, ten categories.",
    blurb: "Meals, walks, weight, meds, vaccinations, symptoms — all in one timeline with pattern-aware warnings. Built for the family that thinks of their dog as family.",
  },
  {
    id: "transporter",
    label: "I'm a transporter",
    desc: "Move dogs cross-country for breeders & buyers.",
    icon: Truck,
    eyebrow: "For transporters",
    headline: "Get listed. Get bookings. Get paid.",
    blurb: "Joining the transport directory is free. You only pay when you deliver — a flat 10% platform fee on each completed trip, escrow-funded so your money's already there.",
  },
  {
    id: "service_provider",
    label: "I'm a service provider",
    desc: "Trainer, groomer, walker, or sitter.",
    icon: Star,
    eyebrow: "For trainers, groomers, walkers & sitters",
    headline: "List free. Keep 88% of every booking.",
    blurb: "Listing in the /find-pros directory is free. You only pay when you book — 12% provider-side app fee on completed sessions. Buyers pay a separate transparent 3% Buyer Protection Fee that funds dispute resolution.",
  },
  {
    id: "storefront_seller",
    label: "I sell food, merch, or services",
    desc: "Open a storefront for your shop.",
    icon: Storefront,
    eyebrow: "For storefront sellers",
    headline: "Your own /store, your own checkout.",
    blurb: "$19/mo (or $179/yr) for unlimited products on /store/{slug}, with Stripe Tax + Connect destination charges built in. 5% platform fee per sale. You keep 89% blended after Stripe processing.",
  },
];

const BREEDER_TIERS = [
  {
    plan: "basic",
    label: "Basic",
    tagline: "Solo breeder — 1 to 2 litters a year.",
    icon: ShieldStar,
    accent: "ink",
    popular: false,
    anchor: "Want Dog Log too? Add $9.99/mo — or upgrade to Pro at $44/mo and get it free, plus unlimited everything, the Verified Pro wax-seal, and a 1% platform-fee discount on every sale.",
    perks: [
      "10 dog registrations + 4 litter registrations / year",
      "Free single-dog marketplace listings (5 active at a time)",
      "Heat-cycle tracker + breeding-window reminders",
      "Public kennel profile with photo gallery",
      "Auto-rendered share cards for every listing (IG / iMessage / FB preview)",
      "30% off Buyer Pass for everyone who buys from you",
      "Standard email support (48hr)",
      "10% platform fee · $25/$40 per Upcoming Litter publish (requires Kennel Verification)",
    ],
  },
  {
    plan: "pro",
    label: "Pro",
    tagline: "Working kennel — 3 to 8 litters a year.",
    icon: Star,
    accent: "gold",
    popular: true,
    perks: [
      "Unlimited dog & litter registrations",
      "Unlimited marketplace listings — including Upcoming Litter waitlists",
      "Verified Pro wax-seal badge on every listing + share card",
      "Free unlimited 4-generation pedigree certificates ($14.99 value each)",
      "Breeder analytics dashboard + premium share-card templates",
      "Dog Log Care Hub included ($9.99/mo value)",
      "9% platform fee on every sale, deposit, and transport (vs. 10% standard)",
      "Buyers earn 75% of every video tour booking (vs. 60% on Basic)",
      "Kennel Verification auto-included",
      "Priority email + chat support · 4hr report SLA",
    ],
  },
  {
    plan: "kennel_elite",
    label: "Kennel Elite",
    tagline: "Serious program — pays for itself in fee savings.",
    icon: Crown,
    accent: "crimson",
    popular: false,
    perks: [
      "Everything in Pro",
      "Elite Kennel wax-seal (crimson + gold) on listings + search results",
      "8% platform fee on every sale, deposit, and transport — save another 1% on every transaction vs Pro",
      "Cohort analytics — see how you stack vs. other Elite kennels in your breed",
      "Lead-source attribution + waitlist auto-nurture pipeline",
      "Reputation defense — right-of-reply + bad-faith-reporter protection",
      "Video hero on your kennel page + pinned testimonials + award case",
      "Featured rotation on Marketplace + Upcoming Litters home (contractual)",
      "Buyers earn 80% of every video tour booking",
      "2 staff sub-accounts + priority phone support (senior account manager)",
    ],
  },
];

const BUYER_PASS_PERKS = [
  "Unlimited breeder inquiries (otherwise rate-limited)",
  "Watchlists for the dogs/litters you're tracking",
  "Save searches with breed-match email alerts",
  "Full 6-generation pedigree viewing (vs. 2-gen for guests)",
  "Priority placement in seller inboxes",
];

const OWNER_PERKS = [
  "Ten care categories: meals, walks, potty, play, energy, weight, meds, symptoms, training, grooming",
  "Pattern-aware warnings (3rd diarrhea entry in a week → vet reminder)",
  "Streaks, stats, and a visual history calendar",
  "Appointment reminders with iCal export",
  "Vet visit & pet-sitter reports (one-click PDF)",
  "Shareable sitter access (no full account needed)",
  "Unlimited dogs on the same plan",
];

const TRANSPORTER_PERKS = [
  "Listed in the public Transport Directory (free)",
  "Buyers & breeders request quotes; you bid",
  "Stripe Connect escrow — buyer funds released on delivery",
  "Rig photos, service-area map, vetted-status badge",
  "Verified badge after 10 completed trips",
  "Disputes resolve by mutual written consent or a 30-day tiered default — Armstrong administers the timer, never the merits",
];

const SERVICE_PROVIDER_PERKS = [
  "Listed in /find-pros directory — free to join",
  "Per-kind onboarding (trainer · groomer · walker · sitter)",
  "Single & recurring bookings up to 26 sessions, auto-charged 24h before each visit",
  "Walkers & sitters: live GPS walk maps (owner-opt-in, in-app only, 24h recap then deleted)",
  "Stripe Connect destination charges — you keep 88%",
  "Separate transparent 3% Buyer Protection Fee paid by customer",
  "Optional trust badges — insurance, pet first aid, breed experience, and a paid $39 background check ($25 credited on first booking). Insurance is strongly recommended for trades where the dog leaves the owner's sight, never required to list.",
  "24h auto-review email lifts your discovery ranking",
  "Disputes resolve by mutual consent or a 30-day default-refund rule — Armstrong does not adjudicate merits",
  "Stripe issues your 1099-K from your Express dashboard — Armstrong files nothing",
];

const STOREFRONT_PERKS = [
  "Your own /store/{slug} with custom branding",
  "$19/mo or $179/yr — unlimited products",
  "Stripe Tax + Connect destination charges built-in",
  "Local-delivery ZIP gating + nationwide shipping toggle",
  "Custom-order quoting flow for one-off requests",
  "Printify integration for on-demand merch",
  "5% platform fee per sale (you keep 89% blended after Stripe)",
  "Featured-rotation slot — $29/30d on /storefronts",
];

const FAQ = [
  {
    q: "Can I cancel anytime?",
    a: "Yes. Cancel from your billing settings — you keep access until the end of the period you've already paid for. We never auto-charge after cancellation.",
  },
  {
    q: "What happens to my dogs/litters if I downgrade?",
    a: "All your registered dogs, litters, and pedigree data stay yours forever — registration is permanent. If you exceed a lower tier's monthly limits, you simply can't register new dogs until you upgrade. Existing records are never deleted.",
  },
  {
    q: "Do I get a refund if I'm not satisfied?",
    a: "Within 14 days of your first plan purchase, full refund — no questions. After that, we'll prorate any unused portion of an annual plan if you cancel early. Per-dog registration fees are non-refundable once the certificate is issued.",
  },
  {
    q: "Can I switch personas later? (e.g. I bought a dog and now I want to breed)",
    a: "Yes. Your account isn't locked to any persona. Upgrade to a breeder plan, add Dog Log on the side, list as a transporter — all from one login. You can also be more than one at once.",
  },
  {
    q: "Is there a free option?",
    a: "Yes. Registering an account, browsing the marketplace, the breeding directory, and the breed catalog is always free. Paid plans unlock the deeper features specific to your persona.",
  },
];

export default function Pricing() {
  // App Store 3.1.1 — no pricing/purchase UI inside the native shell.
  if (isNativeApp()) return <NativePlanNotice />;
  return <PricingWeb />;
}

function PricingWeb() {
  const [plans, setPlans] = useState(null);
  const [billing, setBilling] = useState("monthly");
  const [persona, setPersona] = useState(() => {
    if (typeof window === "undefined") return "breeder";
    const params = new URLSearchParams(window.location.search);
    const want = params.get("for") || "";
    if (PERSONAS.some((p) => p.id === want)) return want;
    return "breeder";
  });
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    api.get("/billing/plans")
      .then((r) => setPlans(r.data || []))
      .catch(() => setPlans([]));
  }, []);

  // Index returned plans by (plan, monthly|annual) so the tiers can
  // pull the right SKU without scanning the array on every render.
  const byPlanInterval = useMemo(() => {
    const out = {};
    (plans || []).forEach((p) => {
      const key = p.plan || p.id;
      const interval = (p.days || 0) >= 180 ? "annual" : "monthly";
      out[`${key}.${interval}`] = p;
    });
    return out;
  }, [plans]);

  const active = PERSONAS.find((p) => p.id === persona) || PERSONAS[0];

  return (
    <div className="min-h-screen bg-cream relative" data-testid="pricing-page">
      <NoiseOverlay />
      <FoundingPromoBanner />

      <div className="max-w-6xl mx-auto px-6 pt-14 pb-6 md:pt-20 relative">
        <ReaderAppBanner />
        <Link
          to="/"
          className="text-[12px] text-ink/60 hover:text-ink inline-flex items-center gap-1 mb-6"
        >
          ← Armstrong K9 Registry
        </Link>
        <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-3">
          Plans &amp; pricing
        </p>
        <h1 className="font-spectral font-light text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-ink max-w-3xl">
          Whatever your relationship to a dog, there's a plan that fits.
        </h1>
        <p className="mt-5 text-ink/70 text-base md:text-lg leading-relaxed max-w-2xl">
          We built this for breeders, buyers, owners, and transporters
          — not just one of them. Tell us which describes you and we'll
          show you the pricing that matters.
        </p>

        {/* Lock-in banner for grandfathered v1 subscribers. Renders
            null for everyone else so the layout is unaffected. */}
        <GrandfatherBanner />

        {/* Persona switcher */}
        <div className="mt-9">
          <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-ink/50 mb-3">
            I am here as a…
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
            {PERSONAS.map((p) => {
              const PIcon = p.icon;
              const isActive = p.id === persona;
              return (
                <button
                  key={p.id}
                  onClick={() => setPersona(p.id)}
                  data-testid={`pricing-persona-${p.id}`}
                  className={`text-left rounded-2xl p-4 border-2 transition-all ${
                    isActive
                      ? "border-[#C9A227] bg-[#FBF7EC] shadow-gold-glow"
                      : "border-stone-200 bg-white hover:border-ink/30"
                  }`}
                >
                  <PIcon
                    size={22}
                    weight="duotone"
                    className={isActive ? "text-[#8A6A00]" : "text-ink/70"}
                  />
                  <p className={`text-[14px] font-semibold mt-2 ${isActive ? "text-ink" : "text-ink/85"}`}>
                    {p.label}
                  </p>
                  <p className="text-[11.5px] text-ink/55 mt-0.5 leading-snug">{p.desc}</p>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Persona-specific section header */}
      <div className="max-w-6xl mx-auto px-6 pt-8 pb-3">
        <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-2">
          {active.eyebrow}
        </p>
        <h2 className="font-spectral font-light text-3xl md:text-4xl tracking-tight leading-tight text-ink">
          {active.headline}
        </h2>
        <p className="mt-3 text-ink/65 text-base leading-relaxed max-w-2xl">
          {active.blurb}
        </p>

        {/* Billing toggle — only relevant for tier-based personas */}
        {(persona === "breeder" || persona === "buyer" || persona === "owner") && (
          <>
            <div className="mt-7 inline-flex items-center bg-white rounded-full border border-stone-200 p-1 shadow-sm">
              <button
                onClick={() => setBilling("monthly")}
                data-testid="pricing-toggle-monthly"
                className={`px-5 py-2 rounded-full text-[13px] font-semibold transition-all ${
                  billing === "monthly" ? "bg-ink text-white" : "text-ink/60 hover:text-ink"
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBilling("annual")}
                data-testid="pricing-toggle-annual"
                className={`px-5 py-2 rounded-full text-[13px] font-semibold transition-all relative ${
                  billing === "annual" ? "bg-ink text-white" : "text-ink/60 hover:text-ink"
                }`}
              >
                Annual
                <span className="ml-2 inline-block bg-emerald-100 text-emerald-800 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded">
                  Save 24–38%
                </span>
              </button>
            </div>
            {billing === "monthly" && (
              <p
                className="mt-3 text-[11.5px] text-ink/50 italic"
                data-testid="pricing-monthly-nudge"
              >
                Most {persona === "breeder" ? "breeders" : persona === "owner" ? "owners" : "members"} start monthly and switch to annual once they're sure — no penalty for switching.
              </p>
            )}
          </>
        )}
      </div>

      {/* Persona content */}
      <div className="max-w-6xl mx-auto px-6 pb-16 pt-6">
        {persona === "breeder" && (
          <BreederTiers byPlanInterval={byPlanInterval} billing={billing} />
        )}
        {persona === "buyer" && (
          <BuyerSection byPlanInterval={byPlanInterval} billing={billing} />
        )}
        {persona === "owner" && (
          <OwnerSection byPlanInterval={byPlanInterval} billing={billing} />
        )}
        {persona === "transporter" && (
          <TransporterSection />
        )}
        {persona === "service_provider" && (
          <ServiceProviderSection />
        )}
        {persona === "storefront_seller" && (
          <StorefrontSellerSection />
        )}
      </div>

      {/* Upcoming Litters fee strip — pay-as-you-go revenue layer that
          sits on top of (any) plan. Surfaces the new 2026-05 product
          on every persona view so breeders and buyers both understand
          the cost of listing / depositing without digging into docs. */}
      {(persona === "breeder" || persona === "buyer") && (
        <div className="max-w-6xl mx-auto px-6 pb-12">
          <UpcomingLittersFeeStrip />
        </div>
      )}

      {/* "Also a…" cross-sell — only shown for non-breeder personas
          so we don't bury breeders in upsells; instead we remind
          other personas the platform serves all of them. */}
      {persona !== "breeder" && (
        <div className="max-w-6xl mx-auto px-6 pb-14">
          <CrossSellStrip currentPersona={persona} setPersona={setPersona} />
        </div>
      )}

      {/* FAQ */}
      <div className="max-w-3xl mx-auto px-6 pb-24">
        <GoldDivider variant="ornament" className="max-w-md mx-auto mb-12" />
        <h2 className="font-spectral font-light text-3xl tracking-tight text-ink text-center mb-2">
          Questions, answered.
        </h2>
        <p className="text-ink/60 text-center mb-10">
          If something's not here, <Link to="/contact" className="text-gold hover:underline">ask us directly</Link>.
        </p>
        <div className="space-y-2">
          {FAQ.map((item, i) => (
            <button
              key={i}
              onClick={() => setOpenFaq(openFaq === i ? null : i)}
              data-testid={`pricing-faq-${i}`}
              className="w-full text-left bg-white rounded-xl border border-stone-200 hover:border-gold/55 transition-colors overflow-hidden"
            >
              <div className="flex items-center justify-between gap-3 p-5">
                <p className="font-semibold text-ink text-[15px]">{item.q}</p>
                <CaretDown
                  size={16}
                  weight="bold"
                  className={`text-ink/40 transition-transform shrink-0 ${openFaq === i ? "rotate-180" : ""}`}
                />
              </div>
              {openFaq === i && (
                <div className="px-5 pb-5 text-[14px] text-ink/70 leading-relaxed">
                  {item.a}
                </div>
              )}
            </button>
          ))}
        </div>

        <div className="text-center mt-16">
          <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-gold mb-3">
            Ready when you are
          </p>
          <h2 className="font-spectral font-light text-3xl md:text-4xl tracking-tight text-ink mb-4">
            Free to start. No card needed.
          </h2>
          <p className="text-ink/65 max-w-lg mx-auto mb-7">
            Sign up free, explore everything, upgrade only when something
            you want to do calls for it.
          </p>
          <Link to="/register">
            <Button
              size="lg"
              data-testid="pricing-final-cta"
              className="bg-ink hover:bg-crimson text-white rounded-full h-12 px-8"
            >
              Create your free account <ArrowRight size={16} className="ml-2" />
            </Button>
          </Link>
          <p className="mt-4 text-[12px] text-ink/55">
            Or <Link to="/gift" className="text-gold underline font-bold" data-testid="pricing-gift-link">gift a subscription to someone</Link> · 12 months, redeemable with a code
          </p>
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// Persona-specific sub-sections
// ===========================================================================

function BreederTiers({ byPlanInterval, billing }) {
  const [foundingSeats, setFoundingSeats] = useState(null);
  useEffect(() => {
    api.get("/pricing/founding-elite")
      .then((r) => setFoundingSeats(r.data))
      .catch(() => {});
  }, []);

  return (
    <div data-testid="pricing-breeder-tiers">
      {foundingSeats && !foundingSeats.is_sold_out && (
        <FoundingEliteBanner data={foundingSeats} />
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
        {BREEDER_TIERS.map((tier) => (
          <TierCard
            key={tier.plan}
            tier={tier}
            sku={byPlanInterval[`${tier.plan}.${billing}`]}
            billing={billing}
          />
        ))}
      </div>
    </div>
  );
}

/** Founding Elite — capped at first 100 Elite signups, $99/mo locked
 *  for 3 years. Placed above the breeder grid so it doesn't compete
 *  with the standard tier layout; instead it acts as a scarcity hook. */
function FoundingEliteBanner({ data }) {
  const remaining = data.remaining;
  const pct = Math.round((data.claimed / data.cap) * 100);
  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-[#3F1D1F] bg-gradient-to-br from-[#1A0F10] via-[#2A1517] to-[#3F1D1F] text-white p-6 md:p-7 mb-6 md:mb-8"
      data-testid="founding-elite-banner"
    >
      <div className="absolute top-3 right-4 text-[10px] font-bold uppercase tracking-[0.25em] text-[#C9A227]">
        Limited · 100 seats
      </div>
      <div className="flex flex-col md:flex-row md:items-center gap-5">
        <div className="flex-1">
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#C9A227] mb-2 inline-flex items-center gap-1.5">
            <Crown size={12} weight="fill" /> Founding Elite
          </p>
          <h3 className="font-spectral font-light text-3xl md:text-4xl tracking-tight leading-[1.05]">
            All Kennel Elite features · <span className="text-[#C9A227]">$99</span><span className="text-base text-white/60">/mo</span>
          </h3>
          <p className="text-[13.5px] text-white/70 mt-2 max-w-xl leading-relaxed">
            Locked at $99/mo for 36 months from signup. Founding Member wax-seal on your kennel + every listing. After year 3, renews at the then-current Kennel Elite rate.
          </p>
          <div className="mt-4">
            <div className="h-1.5 w-full max-w-md bg-white/10 rounded-full overflow-hidden">
              <div
                className="h-full bg-[#C9A227]"
                style={{ width: `${Math.max(pct, 2)}%` }}
              />
            </div>
            <p
              className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#C9A227] mt-2"
              data-testid="founding-elite-seat-counter"
            >
              {remaining} of {data.cap} seats remaining
            </p>
          </div>
        </div>
        <Link to="/register?intent=founding_elite" className="shrink-0">
          <Button
            className="rounded-full h-12 px-6 bg-[#C9A227] hover:bg-[#A8881E] text-ink font-bold"
            data-testid="founding-elite-cta"
          >
            Claim a Founding seat →
          </Button>
        </Link>
      </div>
    </div>
  );
}

function BuyerSection({ byPlanInterval, billing }) {
  const buyerPass = byPlanInterval[`buyer_pass.${billing}`];
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch" data-testid="pricing-buyer-section">
      <FreeTierCard
        label="Just browsing"
        icon={MagnifyingGlass}
        pitch="The whole marketplace, breed catalog, and breeding directory are free to browse. Sign up to message breeders, save dogs to a wishlist, and view 2-generation pedigrees."
        perks={[
          "Browse the full marketplace",
          "Read every public kennel profile",
          "View 2-gen pedigrees",
          "Limited inquiries per week",
        ]}
        ctaText="Sign up free"
        ctaTo="/register"
      />
      <FlagshipCard
        label="Buyer Pass"
        icon={Sparkle}
        sku={buyerPass}
        billing={billing}
        accent="gold"
        pitch="For serious buyers who are actively looking. Removes every guardrail we put on guests so you can move at your own pace."
        perks={BUYER_PASS_PERKS}
        ctaText="Start Buyer Pass"
        ctaTo="/register?intent=buyer"
      />
    </div>
  );
}

function OwnerSection({ byPlanInterval, billing }) {
  const dogLog = byPlanInterval[`care_plus.${billing}`];
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch" data-testid="pricing-owner-section">
      <PerDogCard />
      <FlagshipCard
        label="Dog Log Care Hub"
        icon={HeartStraight}
        sku={dogLog}
        billing={billing}
        accent="gold"
        pitch="The premium care subscription for people who think of their dog as family. Pattern-aware warnings catch problems before they become emergencies."
        perks={OWNER_PERKS}
        ctaText="Start Dog Log"
        ctaTo="/register?intent=owner"
        note="Auto-included with Breeder Pro and Kennel Elite."
      />
    </div>
  );
}

function PerDogCard() {
  return (
    <div className="rounded-2xl border-2 border-stone-200 bg-white p-7 flex flex-col" data-testid="pricing-owner-perdog-card">
      <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-stone-50 border border-stone-200 mb-4">
        <PawPrint size={20} weight="duotone" className="text-ink/70" />
      </div>
      <h3 className="font-spectral font-light text-2xl tracking-tight text-ink">Just register a dog</h3>
      <p className="text-[13px] text-ink/60 mt-1 leading-snug min-h-[3em]">
        Pay-as-you-go. Free to make an account; only pay when you actually register a dog or litter. No subscription needed.
      </p>
      <div className="my-6">
        <p className="font-spectral text-5xl font-light tracking-tight text-ink">
          $25
          <span className="text-base font-sans text-ink/50 ml-1">/ dog</span>
        </p>
        <p className="text-[11px] text-ink/50 mt-1">One-time. Pedigree record is permanent.</p>
      </div>
      <ul className="space-y-2.5 mb-7 flex-1">
        <li className="flex items-start gap-2 text-[13.5px] text-ink/80 leading-snug">
          <CheckCircle size={15} weight="duotone" className="shrink-0 mt-0.5 text-ink/70" />
          <span>Lifetime pedigree record (one-time $25 per dog)</span>
        </li>
        <li className="flex items-start gap-2 text-[13.5px] text-ink/80 leading-snug">
          <CheckCircle size={15} weight="duotone" className="shrink-0 mt-0.5 text-ink/70" />
          <span>Photos, microchip, health certificates on file</span>
        </li>
        <li className="flex items-start gap-2 text-[13.5px] text-ink/80 leading-snug">
          <CheckCircle size={15} weight="duotone" className="shrink-0 mt-0.5 text-ink/70" />
          <span>Whole-litter registration: $75 (covers every puppy)</span>
        </li>
        <li className="flex items-start gap-2 text-[13.5px] text-ink/80 leading-snug">
          <CheckCircle size={15} weight="duotone" className="shrink-0 mt-0.5 text-ink/70" />
          <span>Printable pedigree certificate: $14.99 each</span>
        </li>
        <li className="flex items-start gap-2 text-[13.5px] text-ink/80 leading-snug">
          <CheckCircle size={15} weight="duotone" className="shrink-0 mt-0.5 text-ink/70" />
          <span>No monthly subscription — pay only when you act</span>
        </li>
      </ul>
      <Link to="/register" className="block">
        <Button className="w-full rounded-full h-11 bg-white border border-ink/20 hover:border-gold text-ink">
          Create a free account
        </Button>
      </Link>
    </div>
  );
}

function _unused_OwnerLegacy() {
  // Intentionally empty. Kept as a placeholder slot in case we later
  // want a 3rd column for owner-side add-ons (e.g. Vet Visit Reports).
  return null;
}

function TransporterSection() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch" data-testid="pricing-transporter-section">
      <FreeTierCard
        label="Join the directory"
        icon={Truck}
        pitch="Apply to become a vetted transporter. Free to list. We verify rig + insurance + 2 references before activating your profile."
        perks={[
          "Public listing in /transport directory",
          "Free for your first 90 days",
          "Rig photos, service area, breed familiarity",
          "Get bids on routes that match your service area",
        ]}
        ctaText="Apply now"
        ctaTo="/transport/apply"
      />
      <div className="rounded-2xl border-2 border-[#C9A227] bg-white p-7 shadow-gold-glow">
        <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-[#FBF7EC] border border-[#C9A227]/40 mb-4">
          <Sparkle size={20} weight="duotone" className="text-[#8A6A00]" />
        </div>
        <h3 className="font-spectral font-light text-2xl tracking-tight text-ink">
          You pay only when you deliver.
        </h3>
        <p className="text-[13px] text-ink/65 mt-2 leading-snug">
          No monthly fee. We take a flat 10% platform fee on each completed trip — escrow-funded, released the moment delivery is confirmed.
        </p>
        <div className="my-6 py-5 border-y border-stone-200 text-center">
          <p className="font-spectral text-5xl font-light tracking-tight text-ink">10<span className="text-3xl">%</span></p>
          <p className="text-[11px] text-ink/55 mt-1">per completed trip · no monthly fee</p>
        </div>
        <ul className="space-y-2.5 mb-6">
          {TRANSPORTER_PERKS.map((p, i) => (
            <li key={i} className="flex items-start gap-2 text-[13.5px] text-ink/80 leading-snug">
              <CheckCircle size={15} weight="duotone" className="shrink-0 mt-0.5 text-[#8A6A00]" />
              <span>{p}</span>
            </li>
          ))}
        </ul>
        <Link to="/transport/apply" className="block">
          <Button
            className="w-full rounded-full h-11 bg-ink hover:bg-crimson text-white"
            data-testid="pricing-cta-transporter"
          >
            Apply to join the directory <ArrowRight size={14} className="ml-1.5" />
          </Button>
        </Link>
      </div>
    </div>
  );
}

function ServiceProviderSection() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch" data-testid="pricing-service-provider-section">
      <FreeTierCard
        label="List on /find-pros"
        icon={Scissors}
        pitch="Apply to be listed in the public Service Providers directory. Free to list. Identity verified before approval; insurance &amp; the paid background check are optional trust badges that lift your search rank."
        perks={[
          "Public profile at /provider/{slug}",
          "Identity verification (always)",
          "Optional insurance badge (strongly recommended)",
          "Optional $39 background check ($25 credited on first booking)",
          "Stripe Connect Express onboarding included",
        ]}
        ctaText="Apply as a provider"
        ctaTo="/become-a-provider"
      />
      <div className="rounded-2xl border-2 border-[#C9A227] bg-white p-7 shadow-gold-glow">
        <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-[#FBF7EC] border border-[#C9A227]/40 mb-4">
          <Sparkle size={20} weight="duotone" className="text-[#8A6A00]" />
        </div>
        <h3 className="font-spectral font-light text-2xl tracking-tight text-ink">
          Keep 88% of every booking.
        </h3>
        <p className="text-[13px] text-ink/65 mt-2 leading-snug">
          No monthly fee. We take 12% provider-side; customers pay a transparent 3% Buyer Protection Fee that funds dispute resolution. Stripe Connect direct deposit on every completed session.
        </p>
        <div className="my-6 py-5 border-y border-stone-200 text-center">
          <p className="font-spectral text-5xl font-light tracking-tight text-ink">88<span className="text-3xl">%</span></p>
          <p className="text-[11px] text-ink/55 mt-1">to provider · no monthly fee · 24h auto-charge for recurring</p>
        </div>
        <ul className="space-y-2.5 mb-6">
          {SERVICE_PROVIDER_PERKS.map((p, i) => (
            <li key={i} className="flex items-start gap-2 text-[13.5px] text-ink/80 leading-snug">
              <CheckCircle size={15} weight="duotone" className="shrink-0 mt-0.5 text-[#8A6A00]" />
              <span>{p}</span>
            </li>
          ))}
        </ul>
        <Link to="/become-a-provider" className="block">
          <Button
            className="w-full rounded-full h-11 bg-ink hover:bg-crimson text-white"
            data-testid="pricing-cta-service-provider"
          >
            Apply as a provider <ArrowRight size={14} className="ml-1.5" />
          </Button>
        </Link>
      </div>
    </div>
  );
}

function StorefrontSellerSection() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch" data-testid="pricing-storefront-seller-section">
      <FreeTierCard
        label="Open a Storefront"
        icon={Storefront}
        pitch="Claim your /store/{slug} and start listing products. Stripe Tax + Connect destination charges built in — you remain the merchant of record, taxes auto-collected and remitted on your behalf."
        perks={[
          "Your own /store/{slug} branded page",
          "Unlimited products + custom-order quoting",
          "Local-delivery ZIP gating + nationwide shipping toggle",
          "Stripe Tax + Connect setup in under 10 minutes",
        ]}
        ctaText="Open my storefront"
        ctaTo="/me/store"
      />
      <div className="rounded-2xl border-2 border-[#C9A227] bg-white p-7 shadow-gold-glow">
        <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-[#FBF7EC] border border-[#C9A227]/40 mb-4">
          <Sparkle size={20} weight="duotone" className="text-[#8A6A00]" />
        </div>
        <h3 className="font-spectral font-light text-2xl tracking-tight text-ink">
          $19/mo · keep 89% of every sale.
        </h3>
        <p className="text-[13px] text-ink/65 mt-2 leading-snug">
          Flat $19/month or $179/year for unlimited products. 5% platform fee per sale — Stripe processing on top. You stay the merchant of record; we handle the marketplace + Stripe Tax routing.
        </p>
        <div className="my-6 py-5 border-y border-stone-200 text-center">
          <p className="font-spectral text-5xl font-light tracking-tight text-ink">$19<span className="text-3xl">/mo</span></p>
          <p className="text-[11px] text-ink/55 mt-1">unlimited products · 5% platform fee per sale</p>
        </div>
        <ul className="space-y-2.5 mb-6">
          {STOREFRONT_PERKS.map((p, i) => (
            <li key={i} className="flex items-start gap-2 text-[13.5px] text-ink/80 leading-snug">
              <CheckCircle size={15} weight="duotone" className="shrink-0 mt-0.5 text-[#8A6A00]" />
              <span>{p}</span>
            </li>
          ))}
        </ul>
        <Link to="/me/store" className="block">
          <Button
            className="w-full rounded-full h-11 bg-ink hover:bg-crimson text-white"
            data-testid="pricing-cta-storefront-seller"
          >
            Open my storefront <ArrowRight size={14} className="ml-1.5" />
          </Button>
        </Link>
      </div>
    </div>
  );
}

// ===========================================================================
// Shared card primitives
// ===========================================================================

function TierCard({ tier, sku, billing }) {
  const Icon = tier.icon;
  const accentBg = {
    ink: "bg-stone-50 border-stone-200",
    gold: "bg-[#FBF7EC] border-[#C9A227]",
    crimson: "bg-stone-50 border-stone-200",
  }[tier.accent];
  const accentText = {
    ink: "text-ink",
    gold: "text-[#8A6A00]",
    crimson: "text-[#C1272D]",
  }[tier.accent];
  const ctaCls = tier.popular
    ? "bg-ink hover:bg-crimson text-white"
    : "bg-white border border-ink/20 hover:border-gold text-ink";

  const price = sku?.amount ?? null;
  const interval = billing === "annual" ? "year" : "month";

  return (
    <div
      className={`relative rounded-2xl border-2 bg-white p-7 flex flex-col ${
        tier.popular ? "border-[#C9A227] shadow-gold-glow" : "border-stone-200"
      }`}
      data-testid={`pricing-tier-${tier.plan}`}
    >
      {tier.popular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#C9A227] text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full">
          Most popular
        </div>
      )}
      <div className={`inline-flex items-center justify-center w-11 h-11 rounded-xl border ${accentBg} mb-4`}>
        <Icon size={20} weight="duotone" className={accentText} />
      </div>
      <h3 className="font-spectral font-light text-2xl tracking-tight text-ink">{tier.label}</h3>
      <p className="text-[13px] text-ink/60 mt-1 leading-snug min-h-[2.4em]">{tier.tagline}</p>

      <div className="my-6">
        {price != null ? (
          <>
            <p className="font-spectral text-5xl font-light tracking-tight text-ink">
              ${Math.floor(price)}
              <span className="text-base font-sans text-ink/50">
                {price % 1 ? `.${Math.round((price % 1) * 100)}` : ""}
              </span>
              <span className="text-base font-sans text-ink/50 ml-1">/ {interval}</span>
            </p>
            {billing === "annual" && (
              <p className="text-[11px] text-emerald-700 font-semibold mt-1">
                Billed yearly — ${(price / 12).toFixed(2)}/mo equivalent
              </p>
            )}
          </>
        ) : (
          <div className="h-16 bg-stone-100 rounded animate-pulse" />
        )}
      </div>

      <ul className="space-y-2.5 mb-7 flex-1">
        {tier.perks.map((p, i) => (
          <li key={i} className="flex items-start gap-2 text-[13.5px] text-ink/80 leading-snug">
            <CheckCircle size={15} weight="duotone" className={`shrink-0 mt-0.5 ${accentText}`} />
            <span>{p}</span>
          </li>
        ))}
      </ul>

      {tier.anchor && (
        <div
          className="rounded-lg bg-stone-50 border border-stone-200 px-3 py-2.5 mb-5 text-[11.5px] text-ink/65 leading-snug"
          data-testid={`pricing-anchor-${tier.plan}`}
        >
          <span className="font-semibold text-ink/80">Tip · </span>{tier.anchor}
        </div>
      )}

      <Link to={tier.plan === "pro" ? "/register?intent=pro_trial" : "/register"} className="block">
        <Button className={`w-full rounded-full h-11 ${ctaCls}`} data-testid={`pricing-cta-${tier.plan}`}>
          {tier.plan === "pro" ? "Start 14-day free trial" : `Get started with ${tier.label}`}
        </Button>
      </Link>
      {tier.plan === "pro" && (
        <p className="text-[10.5px] text-ink/55 mt-2 text-center" data-testid="pro-trial-disclaimer">
          Card on file. No charge for 14 days. Cancel anytime.
        </p>
      )}
    </div>
  );
}

function FreeTierCard({ label, icon: Icon, pitch, perks, ctaText, ctaTo, footnote }) {
  return (
    <div className="rounded-2xl border-2 border-stone-200 bg-white p-7 flex flex-col">
      <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-stone-50 border border-stone-200 mb-4">
        <Icon size={20} weight="duotone" className="text-ink/70" />
      </div>
      <h3 className="font-spectral font-light text-2xl tracking-tight text-ink">{label}</h3>
      <p className="text-[13px] text-ink/60 mt-1 leading-snug min-h-[3em]">{pitch}</p>
      <div className="my-6">
        <p className="font-spectral text-5xl font-light tracking-tight text-ink">Free</p>
        <p className="text-[11px] text-ink/50 mt-1">No card needed</p>
      </div>
      <ul className="space-y-2.5 mb-7 flex-1">
        {perks.map((p, i) => (
          <li key={i} className="flex items-start gap-2 text-[13.5px] text-ink/80 leading-snug">
            <CheckCircle size={15} weight="duotone" className="shrink-0 mt-0.5 text-ink/70" />
            <span>{p}</span>
          </li>
        ))}
      </ul>
      {footnote && <p className="text-[11px] text-ink/50 italic mb-3">{footnote}</p>}
      <Link to={ctaTo} className="block">
        <Button className="w-full rounded-full h-11 bg-white border border-ink/20 hover:border-gold text-ink">
          {ctaText}
        </Button>
      </Link>
    </div>
  );
}

function FlagshipCard({ label, icon: Icon, sku, billing, pitch, perks, ctaText, ctaTo, note }) {
  const price = sku?.amount ?? null;
  const interval = billing === "annual" ? "year" : "month";
  return (
    <div className="relative rounded-2xl border-2 border-[#C9A227] bg-white p-7 flex flex-col shadow-gold-glow">
      <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#C9A227] text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full">
        Most popular
      </div>
      <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-[#FBF7EC] border border-[#C9A227]/40 mb-4">
        <Icon size={20} weight="duotone" className="text-[#8A6A00]" />
      </div>
      <h3 className="font-spectral font-light text-2xl tracking-tight text-ink">{label}</h3>
      <p className="text-[13px] text-ink/60 mt-1 leading-snug min-h-[3em]">{pitch}</p>
      <div className="my-6">
        {price != null ? (
          <>
            <p className="font-spectral text-5xl font-light tracking-tight text-ink">
              ${Math.floor(price)}
              <span className="text-base font-sans text-ink/50">
                {price % 1 ? `.${Math.round((price % 1) * 100)}` : ""}
              </span>
              <span className="text-base font-sans text-ink/50 ml-1">/ {interval}</span>
            </p>
            {billing === "annual" && (
              <p className="text-[11px] text-emerald-700 font-semibold mt-1">
                Billed yearly — ${(price / 12).toFixed(2)}/mo equivalent
              </p>
            )}
          </>
        ) : (
          <div className="h-16 bg-stone-100 rounded animate-pulse" />
        )}
      </div>
      <ul className="space-y-2.5 mb-6 flex-1">
        {perks.map((p, i) => (
          <li key={i} className="flex items-start gap-2 text-[13.5px] text-ink/80 leading-snug">
            <CheckCircle size={15} weight="duotone" className="shrink-0 mt-0.5 text-[#8A6A00]" />
            <span>{p}</span>
          </li>
        ))}
      </ul>
      {note && (
        <p className="text-[11px] text-ink/55 italic mb-3 leading-snug">{note}</p>
      )}
      <Link to={ctaTo} className="block">
        <Button className="w-full rounded-full h-11 bg-ink hover:bg-crimson text-white">
          {ctaText}
        </Button>
      </Link>
    </div>
  );
}

function CrossSellStrip({ currentPersona, setPersona }) {
  const others = PERSONAS.filter((p) => p.id !== currentPersona);
  return (
    <div className="rounded-2xl border border-stone-200 bg-white p-6">
      <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-ink/50 mb-3">
        Also a…?
      </p>
      <p className="text-[14px] text-ink/70 mb-5 leading-relaxed max-w-2xl">
        You can wear more than one hat. Switch tabs above anytime, or jump straight to another persona's pricing here:
      </p>
      <div className="flex flex-wrap gap-2.5">
        {others.map((p) => {
          const PIcon = p.icon;
          return (
            <button
              key={p.id}
              onClick={() => {
                setPersona(p.id);
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              data-testid={`pricing-cross-sell-${p.id}`}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-stone-200 bg-white hover:border-gold hover:bg-[#FBF7EC] text-[13px] text-ink transition-all"
            >
              <PIcon size={14} weight="duotone" className="text-[#8A6A00]" />
              <span>{p.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ===========================================================================
// Upcoming Litters — pay-as-you-go fee strip (2026-05 product)
// ===========================================================================
function UpcomingLittersFeeStrip() {
  const ROWS = [
    {
      title: "Planned breeding listing",
      price: "$25",
      sub: "one-time · non-refundable",
      copy:
        "Publish a sire × dam pairing before the breeding is confirmed.",
    },
    {
      title: "Expected litter listing",
      price: "$40",
      sub: "one-time · non-refundable",
      copy:
        "Upgrade after the breeding is confirmed. Listing fee paid once per litter.",
    },
    {
      title: "Featured boost (30 days)",
      price: "$15",
      sub: "optional, per listing",
      copy:
        "Pin to the top of the upcoming-litters marketplace for 30 days.",
    },
    {
      title: "Deposit platform fee",
      price: "5%",
      sub: "capped at $25 per deposit",
      copy:
        "Buyers pay deposits directly to you via Stripe Connect. Armstrong takes a small fee on top — you're the merchant of record.",
    },
  ];
  return (
    <div
      className="rounded-2xl border-2 border-[#C9A227] bg-white p-7"
      data-testid="pricing-upcoming-strip"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#8A6A00] mb-2">
            Upcoming Litters · pay-as-you-go
          </p>
          <h3 className="font-spectral font-light text-2xl tracking-tight text-ink">
            List planned breedings, collect waitlist deposits, host signable contracts.
          </h3>
          <p className="text-[13.5px] text-ink/65 mt-1 leading-snug max-w-2xl">
            No subscription. Pay only when you publish a listing. Deposits
            flow directly to your Stripe Connect account — Armstrong is a
            venue, not a party to your sales contract.
          </p>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-3">
        {ROWS.map((r, i) => (
          <div
            key={i}
            className="rounded-xl border border-stone-200 bg-[#FAFAFA] p-4"
          >
            <p className="font-spectral text-3xl font-light text-ink leading-none">
              {r.price}
            </p>
            <p className="text-[10px] uppercase tracking-[0.18em] text-ink/55 mt-1.5">
              {r.sub}
            </p>
            <p className="text-[13px] text-ink font-bold mt-3">{r.title}</p>
            <p className="text-[12px] text-ink/65 mt-1 leading-snug">{r.copy}</p>
          </div>
        ))}
      </div>
      <p className="text-[11px] text-ink/50 mt-4">
        8-week earliest-go-home rule auto-enforced. Breeder uploads their own
        lawyer-reviewed contract; buyers sign click-wrap after paying deposit.
      </p>
    </div>
  );
}

